/**
 * TradeVisualizer v8
 *
 * ROOT CAUSE OF ALL PREVIOUS FLICKERING FIXED:
 * context prop was a new object every parent re-render (useApi polling ~5s).
 * useEffect([trade, context]) re-ran → setChartData({}) → blank chart → re-fetch.
 * Fix: derive a stable string key from actual values, use that as effect dep.
 *
 * Also adds multi-TF BoS/ChoCH signals (Entry-TF, Context-TF, Daily).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fonts } from "../theme";
import { API_BASE } from "../config";

// ── THEMES ────────────────────────────────────────────────────────────────────
const DARK = {
  bg:"#0d1117",panel:"#0f172a",header:"#080d14",
  border:"rgba(255,255,255,0.07)",grid:"rgba(255,255,255,0.04)",
  text:"rgba(255,255,255,0.45)",textS:"rgba(255,255,255,0.9)",textF:"rgba(255,255,255,0.2)",
  bull:"#26a69a",bear:"#ef5350",entry:"#00e676",exit:"#40c4ff",sl:"#ff5252",
  bos:"#ffb300",choch:"#ce93d8",
  entryFill:"rgba(0,230,118,0.055)",slFill:"rgba(255,82,82,0.055)",zone:"rgba(255,255,255,0.02)",
  xhair:"rgba(255,255,255,0.3)",axisBg:"#1e2d40",axisText:"#e2e8f0",dark:true,
};
const LIGHT = {
  bg:"#f8fafc",panel:"#ffffff",header:"#f1f5f9",
  border:"rgba(0,0,0,0.08)",grid:"rgba(0,0,0,0.055)",
  text:"rgba(0,0,0,0.4)",textS:"rgba(0,0,0,0.88)",textF:"rgba(0,0,0,0.22)",
  bull:"#0d9488",bear:"#dc2626",entry:"#16a34a",exit:"#2563eb",sl:"#dc2626",
  bos:"#d97706",choch:"#7c3aed",
  entryFill:"rgba(22,163,74,0.07)",slFill:"rgba(220,38,38,0.06)",zone:"rgba(0,0,0,0.02)",
  xhair:"rgba(0,0,0,0.25)",axisBg:"#e2e8f0",axisText:"#0f172a",dark:false,
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
const pMs  = s => { if(!s) return null; const ms=Date.parse(s.includes("T")?s:s.replace(" ","T")+":00Z"); return isNaN(ms)?null:ms; };
const fT   = s => { const ms=pMs(s); return ms?new Date(ms).toLocaleString("de-DE",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit",timeZone:"UTC"}):"—"; };
const fD   = (a,b) => { const da=pMs(a),db=pMs(b); if(!da||!db) return "—"; const m=Math.floor(Math.abs(db-da)/60000); return m>=60?`${Math.floor(m/60)}h ${m%60}m`:`${m}m`; };
const nS   = (r,n=7) => { const raw=r/n,e=Math.pow(10,Math.floor(Math.log10(raw))); return ([1,2,2.5,5,10].find(x=>x*e>=raw)||10)*e; };
const near = (arr,ms) => { if(!arr?.length||!ms) return -1; let b=-1,bD=Infinity; arr.forEach((c,i)=>{const d=Math.abs(c.t-ms);if(d<bD){bD=d;b=i;}}); return b; };
const PAD = {T:32,R:80,B:44,L:8};

function autoV(candles,et) {
  const len=candles?.length||0; if(!len) return {start:0,count:1};
  const eIdx=near(candles,pMs(et)), count=Math.min(len,150);
  return {start:Math.max(0,Math.min(len-count,Math.round(eIdx-count*0.65))),count};
}
function clamp(v,len) {
  return {start:Math.max(0,Math.min(len-1,v.start)),count:Math.max(15,Math.min(len,v.count))};
}

// ── DRAW FUNCTIONS ────────────────────────────────────────────────────────────
function drawChart(ctx, W, H, vis, eIdx, xIdx, ep, xp, sl, dir, tf, sigs, C) {
  const cW=W-PAD.L-PAD.R, cH=H-PAD.T-PAD.B;
  ctx.clearRect(0,0,W,H); ctx.fillStyle=C.bg; ctx.fillRect(0,0,W,H);
  if(!vis.length) return;

  const lvls=[ep,xp,sl].filter(Boolean);
  let hi=Math.max(...vis.map(c=>c.h),...lvls), lo=Math.min(...vis.map(c=>c.l),...lvls);
  const rng=hi-lo; hi+=rng*0.10; lo-=rng*0.08;
  const tR=hi-lo, bW=cW/vis.length, cndW=Math.max(1,bW*0.72);
  const toY=p=>PAD.T+cH*(1-(p-lo)/tR), toX=i=>PAD.L+(i+0.5)*bW;
  const step=nS(tR), sp=Math.ceil(lo/step)*step;

  ctx.strokeStyle=C.border; ctx.lineWidth=1; ctx.strokeRect(PAD.L,PAD.T,cW,cH);
  ctx.save(); ctx.beginPath(); ctx.rect(PAD.L,PAD.T,cW,cH); ctx.clip();

  // Grid
  for(let p=sp;p<=hi+step*0.01;p=Math.round((p+step)*1e8)/1e8){
    ctx.strokeStyle=C.grid; ctx.lineWidth=1;
    ctx.beginPath();ctx.moveTo(PAD.L,toY(p));ctx.lineTo(PAD.L+cW,toY(p));ctx.stroke();
  }
  const vs=Math.max(1,Math.round(55/bW));
  for(let i=0;i<vis.length;i+=vs){
    ctx.strokeStyle=C.grid; ctx.lineWidth=1;
    ctx.beginPath();ctx.moveTo(toX(i),PAD.T);ctx.lineTo(toX(i),PAD.T+cH);ctx.stroke();
  }

  // Zones
  if(eIdx>=0&&xIdx>=0){ctx.fillStyle=C.zone;ctx.fillRect(PAD.L+eIdx*bW,PAD.T,(xIdx-eIdx+1)*bW,cH);}
  if(sl&&ep){const y1=toY(Math.max(ep,sl)),y2=toY(Math.min(ep,sl));ctx.fillStyle=C.slFill;ctx.fillRect(PAD.L,y1,cW,Math.max(1,y2-y1));}
  if(ep&&xp){const y1=toY(Math.max(ep,xp)),y2=toY(Math.min(ep,xp));ctx.fillStyle=C.entryFill;ctx.fillRect(PAD.L,y1,cW,Math.max(1,y2-y1));}

  // ── BoS / ChoCH: horizontal dashed line + centered label ──────────────────
  const sigsToDraw = [];
  sigs.forEach(sig => {
    const breakMs  = pMs(sig.time);
    const startMs  = sig.level_start ? pMs(sig.level_start) : null;
    const breakIdx = near(vis, breakMs);
    if (breakIdx < 0) return;
    const startIdx = startMs ? Math.max(0, near(vis, startMs)) : Math.max(0, breakIdx - 20);
    const y = toY(sig.price);
    if (y < PAD.T - 2 || y > PAD.T + cH + 2) return;
    sigsToDraw.push({ ...sig, breakIdx, startIdx, y });
  });

  sigsToDraw.forEach(({ type, breakIdx, startIdx, y }) => {
    const col  = type === "CHoCH" ? C.choch : C.bos;
    const x1   = toX(startIdx);
    const x2   = toX(breakIdx);
    const midX = (x1 + x2) / 2;

    ctx.font = `bold 8.5px ${fonts.mono}`;
    const tw = ctx.measureText(type).width + 10;
    const th = 14;
    const lx = midX - tw / 2;
    const ly = y - th / 2;

    // Dashed line — draw in two segments, leaving a gap for the label
    ctx.save();
    ctx.beginPath(); ctx.rect(PAD.L, PAD.T, cW, cH); ctx.clip();
    ctx.strokeStyle = col; ctx.lineWidth = 1.5; ctx.globalAlpha = 0.7;
    ctx.setLineDash([5, 3]);
    // Left segment
    ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(lx - 3, y); ctx.stroke();
    // Right segment
    ctx.beginPath(); ctx.moveTo(lx + tw + 3, y); ctx.lineTo(x2, y); ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
    ctx.restore();

    // Centered label — clamp so it never leaves the chart area
    const clampedLx = Math.max(PAD.L + 2, Math.min(PAD.L + cW - tw - 2, lx));
    ctx.fillStyle = col;
    ctx.fillRect(clampedLx, ly, tw, th);
    ctx.fillStyle = "#fff";
    ctx.textAlign = "left";
    ctx.fillText(type, clampedLx + 5, y + 3.5);
  });

  // Level lines
  const hL=(p,col,dashed,lw=1)=>{
    if(!p) return; const y=toY(p); if(y<PAD.T-2||y>PAD.T+cH+2) return;
    ctx.save();ctx.strokeStyle=col;ctx.lineWidth=lw;ctx.globalAlpha=0.9;
    if(dashed)ctx.setLineDash([5,5]);
    ctx.beginPath();ctx.moveTo(PAD.L,y);ctx.lineTo(PAD.L+cW,y);ctx.stroke();
    ctx.setLineDash([]);ctx.restore();
  };
  hL(ep,C.entry,false,1.5); hL(xp,C.exit,true,1); hL(sl,C.sl,true,1.5);

  // Candles
  vis.forEach((c,i)=>{
    const x=toX(i),up=c.c>=c.o,col=up?C.bull:C.bear;
    ctx.strokeStyle=col;ctx.lineWidth=1;
    ctx.beginPath();ctx.moveTo(x,toY(c.h));ctx.lineTo(x,toY(c.l));ctx.stroke();
    const bT=Math.min(toY(c.o),toY(c.c)),bH=Math.max(1,Math.abs(toY(c.c)-toY(c.o)));
    ctx.fillStyle=col;ctx.fillRect(x-cndW/2,bT,cndW,bH);
  });

  // Vertical dash lines
  const vD=(idx,col)=>{if(idx<0)return;ctx.strokeStyle=col;ctx.globalAlpha=0.38;ctx.lineWidth=1;ctx.setLineDash([4,4]);ctx.beginPath();ctx.moveTo(toX(idx),PAD.T);ctx.lineTo(toX(idx),PAD.T+cH);ctx.stroke();ctx.setLineDash([]);ctx.globalAlpha=1;};
  vD(eIdx,C.entry); if(xIdx!==eIdx)vD(xIdx,C.exit);

  // Entry arrow
  if(eIdx>=0&&ep){
    const x=toX(eIdx),py=toY(ep),lng=dir==="long";
    ctx.fillStyle=C.entry;ctx.beginPath();
    if(lng){ctx.moveTo(x,py+8);ctx.lineTo(x-9,py+24);ctx.lineTo(x+9,py+24);}
    else{ctx.moveTo(x,py-8);ctx.lineTo(x-9,py-24);ctx.lineTo(x+9,py-24);}
    ctx.closePath();ctx.fill();
    ctx.font=`bold 9px ${fonts.mono}`;ctx.fillStyle=C.dark?"#fff":"#000";ctx.textAlign="center";
    ctx.fillText("E",x,lng?py+20:py-16);
  }
  // Exit diamond
  if(xIdx>=0&&xp){
    const x=toX(xIdx),py=toY(xp),s=8;
    ctx.fillStyle=C.exit;ctx.beginPath();ctx.moveTo(x,py-s);ctx.lineTo(x+s,py);ctx.lineTo(x,py+s);ctx.lineTo(x-s,py);ctx.closePath();ctx.fill();
    ctx.strokeStyle=C.dark?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.2)";ctx.lineWidth=0.75;ctx.stroke();
  }

  ctx.restore();

  // Axes
  ctx.font=`10.5px ${fonts.mono}`; ctx.textAlign="left"; ctx.fillStyle=C.text;
  for(let p=sp;p<=hi+step*0.01;p=Math.round((p+step)*1e8)/1e8){
    const y=toY(p); if(y<PAD.T-4||y>PAD.T+cH+4) continue;
    ctx.fillText(p.toFixed(2),PAD.L+cW+6,y+3.5);
  }
  const aL=(p,col,lbl)=>{
    if(!p)return;const y=toY(p);if(y<PAD.T-4||y>PAD.T+cH+4)return;
    ctx.fillStyle=col;ctx.fillRect(PAD.L+cW+1,y-9,PAD.R-3,18);
    ctx.fillStyle=C.axisText;ctx.font=`bold 9px ${fonts.mono}`;ctx.textAlign="left";
    ctx.fillText(`${lbl} ${p.toFixed(2)}`,PAD.L+cW+4,y+4);
  };
  aL(ep,C.entry,"E"); aL(xp,C.exit,"X"); if(sl)aL(sl,C.sl,"SL");

  // Time axis
  ctx.fillStyle=C.text;ctx.font=`9.5px ${fonts.mono}`;ctx.textAlign="center";
  const ls=Math.max(1,Math.ceil(vis.length/Math.max(1,Math.floor(cW/100))));
  vis.forEach((c,i)=>{
    if(i%ls!==0)return;
    const d=new Date(c.t);
    let lbl;
    if(tf==="D"||tf==="W") lbl=d.toLocaleDateString("de-DE",{month:"short",day:"numeric"});
    else if(["H4","H2","H1"].includes(tf)) lbl=`${d.toLocaleDateString("de-DE",{month:"2-digit",day:"2-digit"})} ${String(d.getUTCHours()).padStart(2,"0")}h`;
    else lbl=`${String(d.getUTCHours()).padStart(2,"0")}:${String(d.getUTCMinutes()).padStart(2,"0")}`;
    ctx.fillText(lbl,toX(i),H-6);
  });
}

function drawXhair(ctx, W, H, vis, hov, C) {
  if(!hov||!vis.length) return;
  const cW=W-PAD.L-PAD.R, cH=H-PAD.T-PAD.B;
  if(hov.x<PAD.L||hov.x>PAD.L+cW) return;
  const lvls=[]; let hi=Math.max(...vis.map(c=>c.h),...lvls), lo=Math.min(...vis.map(c=>c.l),...lvls);
  const rng=hi-lo; hi+=rng*0.10; lo-=rng*0.08;
  const tR=hi-lo, bW=cW/vis.length, toY=p=>PAD.T+cH*(1-(p-lo)/tR), toX=i=>PAD.L+(i+0.5)*bW;

  ctx.strokeStyle=C.xhair; ctx.lineWidth=1; ctx.setLineDash([3,4]);
  ctx.beginPath();ctx.moveTo(hov.x,PAD.T);ctx.lineTo(hov.x,PAD.T+cH);ctx.stroke();
  ctx.beginPath();ctx.moveTo(PAD.L,hov.y);ctx.lineTo(PAD.L+cW,hov.y);ctx.stroke();
  ctx.setLineDash([]);
  if(hov.y>PAD.T&&hov.y<PAD.T+cH){
    const p=lo+(1-(hov.y-PAD.T)/cH)*tR;
    ctx.fillStyle=C.axisBg;ctx.fillRect(PAD.L+cW,hov.y-10,PAD.R,20);
    ctx.strokeStyle=C.xhair;ctx.lineWidth=1;ctx.strokeRect(PAD.L+cW,hov.y-10,PAD.R,20);
    ctx.fillStyle=C.axisText;ctx.font=`bold 10px ${fonts.mono}`;ctx.textAlign="left";
    ctx.fillText(p.toFixed(2),PAD.L+cW+5,hov.y+3.5);
  }
  if(hov.idx!=null&&vis[hov.idx]){
    const x=toX(hov.idx), d=new Date(vis[hov.idx].t);
    const lbl=`${d.toLocaleDateString("de-DE",{month:"2-digit",day:"2-digit"})} ${String(d.getUTCHours()).padStart(2,"0")}:${String(d.getUTCMinutes()).padStart(2,"0")}`;
    ctx.font=`9.5px ${fonts.mono}`;ctx.textAlign="center";
    const tw=ctx.measureText(lbl).width+14;
    ctx.fillStyle=C.axisBg;ctx.fillRect(x-tw/2,H-PAD.B+3,tw,17);
    ctx.strokeStyle=C.xhair;ctx.lineWidth=1;ctx.strokeRect(x-tw/2,H-PAD.B+3,tw,17);
    ctx.fillStyle=C.axisText;ctx.fillText(lbl,x,H-PAD.B+14);
  }
}

// ── CANVAS CHART ──────────────────────────────────────────────────────────────
function CandleChart({ candles, entryTime, exitTime, entryPrice, exitPrice, initialSl, direction, tf, signals, C }) {
  const canvasRef    = useRef(null);
  const backingRef   = useRef(null);
  const containerRef = useRef(null);
  const ohlcRef      = useRef(null);
  const [dim, setDim]= useState({w:1100,h:560});

  // All mutable render state in one ref (RAF reads this, no React re-renders needed)
  const R = useRef({ dim:{w:1100,h:560}, candles:[], entryTime:null, exitTime:null, ep:0, xp:0, sl:null, dir:"long", tf:"M5", signals:[], C:DARK, view:null, hover:null, drag:null, dirty:true });

  // Sync props → R.current + mark dirty (no array re-creation here → no spurious firing)
  useEffect(() => {
    Object.assign(R.current, {candles:candles||[], entryTime, exitTime, ep:entryPrice, xp:exitPrice, sl:initialSl, dir:direction, tf, signals:signals||[], C, dirty:true});
    R.current.view = null;
  }, [candles, entryTime, exitTime, entryPrice, exitPrice, initialSl, direction, tf, signals, C]);

  // Resize observer
  useEffect(() => {
    if(!containerRef.current) return;
    const obs=new ResizeObserver(([e])=>{
      const {width:w,height:h}=e.contentRect;
      if(w>20&&h>20) setDim({w:Math.floor(w),h:Math.floor(h)});
    });
    obs.observe(containerRef.current);
    return()=>obs.disconnect();
  }, []);

  // Sync dim → R + canvas resize
  useEffect(() => {
    if(!canvasRef.current) return;
    canvasRef.current.width=dim.w; canvasRef.current.height=dim.h;
    R.current.dim=dim; R.current.dirty=true;
  }, [dim]);

  // RAF loop — empty deps, NEVER restarts → zero blank-frame flicker
  useEffect(() => {
    backingRef.current = document.createElement("canvas");
    let alive=true;
    const raf={id:null};

    function frame() {
      if(!alive) return;
      const canvas=canvasRef.current, backing=backingRef.current;
      if(!canvas||!backing){raf.id=requestAnimationFrame(frame);return;}

      const {dim:d,candles:cs,entryTime:et,exitTime:xt,ep,xp,sl,dir,tf:t,signals:sigs,C:theme,hover,dirty}=R.current;
      const len=cs.length, auto=autoV(cs,et);
      const view=R.current.view?clamp(R.current.view,len):clamp(auto,len);
      const vis=cs.slice(view.start,view.start+view.count);
      const eIdx=near(vis,pMs(et)), xIdx=near(vis,pMs(xt));

      const ctx=canvas.getContext("2d");

      if(dirty){
        if(backing.width!==d.w||backing.height!==d.h){backing.width=d.w;backing.height=d.h;}
        drawChart(backing.getContext("2d"),d.w,d.h,vis,eIdx,xIdx,ep,xp,sl,dir,t,sigs,theme);
        R.current.dirty=false;
      }

      ctx.clearRect(0,0,d.w,d.h);
      ctx.drawImage(backing,0,0);
      if(hover) drawXhair(ctx,d.w,d.h,vis,hover,theme);

      raf.id=requestAnimationFrame(frame);
    }

    raf.id=requestAnimationFrame(frame);
    return()=>{alive=false;cancelAnimationFrame(raf.id);};
  }, []); // ← INTENTIONALLY EMPTY

  // Mouse: pure ref mutations
  const onMove = useCallback((e)=>{
    const canvas=canvasRef.current; if(!canvas) return;
    const rect=canvas.getBoundingClientRect();
    const {dim:d,candles:cs,entryTime:et}=R.current;
    const x=(e.clientX-rect.left)*(d.w/rect.width), y=(e.clientY-rect.top)*(d.h/rect.height);
    const view=R.current.view?clamp(R.current.view,cs.length):clamp(autoV(cs,et),cs.length);
    const cW=d.w-PAD.L-PAD.R, bW=cW/view.count;
    const idx=Math.max(0,Math.min(view.count-1,Math.floor((x-PAD.L)/bW)));
    R.current.hover={x,y,idx};
    if(R.current.drag){
      const bpp=view.count/cW;
      const delta=Math.round((e.clientX-R.current.drag.startX)*(d.w/rect.width)*bpp);
      const ns=Math.max(0,Math.min(cs.length-view.count,R.current.drag.viewStart-delta));
      if(ns!==view.start){R.current.view={start:ns,count:view.count};R.current.dirty=true;}
    }
    const vis=cs.slice(view.start,view.start+view.count);
    const c=vis[idx];
    if(ohlcRef.current&&c){
      const up=c.c>=c.o,th=R.current.C;
      ohlcRef.current.innerHTML=`<span style="opacity:.4">O</span>&nbsp;<b>${c.o.toFixed(2)}</b>&ensp;<span style="opacity:.4">H</span>&nbsp;<b style="color:${th.bull}">${c.h.toFixed(2)}</b>&ensp;<span style="opacity:.4">L</span>&nbsp;<b style="color:${th.bear}">${c.l.toFixed(2)}</b>&ensp;<span style="opacity:.4">C</span>&nbsp;<b style="color:${up?th.bull:th.bear}">${c.c.toFixed(2)}</b>`;
    }
  }, []);

  const onLeave  = useCallback(()=>{R.current.hover=null;R.current.drag=null;if(ohlcRef.current)ohlcRef.current.innerHTML="";},[]);
  const onDown   = useCallback((e)=>{const {candles:cs,entryTime:et}=R.current;const view=R.current.view?clamp(R.current.view,cs.length):clamp(autoV(cs,et),cs.length);R.current.drag={startX:e.clientX,viewStart:view.start};e.preventDefault();},[]);
  const onUp     = useCallback(()=>{R.current.drag=null;},[]);
  const onWheel  = useCallback((e)=>{
    e.preventDefault();
    const {candles:cs,entryTime:et}=R.current; const len=cs.length; if(!len)return;
    const view=R.current.view?clamp(R.current.view,len):clamp(autoV(cs,et),len);
    const nc=Math.max(15,Math.min(len,Math.round(view.count*(e.deltaY>0?1.15:0.87))));
    const rect=containerRef.current?.getBoundingClientRect();
    const fx=rect?(e.clientX-rect.left)/rect.width:0.5;
    const pivot=view.start+fx*view.count;
    const ns=Math.max(0,Math.min(len-nc,Math.round(pivot-fx*nc)));
    R.current.view={start:ns,count:nc}; R.current.dirty=true;
  },[]);

  useEffect(()=>{
    const el=containerRef.current; if(!el) return;
    el.addEventListener("wheel",onWheel,{passive:false});
    return()=>el.removeEventListener("wheel",onWheel);
  },[onWheel]);

  return (
    <div ref={containerRef} style={{width:"100%",height:"100%",position:"relative",overflow:"hidden",userSelect:"none"}}>
      <canvas ref={canvasRef} style={{position:"absolute",inset:0,width:"100%",height:"100%",cursor:"crosshair"}}
        onMouseMove={onMove} onMouseLeave={onLeave} onMouseDown={onDown} onMouseUp={onUp}/>
      <div ref={ohlcRef} style={{position:"absolute",top:9,left:13,fontSize:12,fontFamily:fonts.mono,color:"rgba(160,170,180,0.9)",pointerEvents:"none",userSelect:"none",letterSpacing:"-0.3px"}}/>
      <div style={{position:"absolute",bottom:50,right:88,fontSize:9,color:"rgba(150,160,170,0.35)",fontFamily:fonts.mono,pointerEvents:"none"}}>scroll·drag</div>
    </div>
  );
}

// ── SIDEBAR ───────────────────────────────────────────────────────────────────
const SL = ({t,C}) => <div style={{fontSize:10,fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:C.textF,marginBottom:8,fontFamily:fonts.mono}}>{t}</div>;
const SR = ({l,v,c,C}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
    <span style={{fontSize:11.5,color:C.textF,fontFamily:fonts.mono}}>{l}</span>
    <span style={{fontSize:13,fontWeight:700,fontFamily:fonts.mono,color:c||C.textS}}>{v??'—'}</span>
  </div>
);
const SB = ({t,children,C}) => (
  <div style={{marginBottom:20,paddingBottom:18,borderBottom:`1px solid ${C.border}`}}>
    <SL t={t} C={C}/>{children}
  </div>
);
const XBar = ({l,v,max,col,C}) => {
  const p=max>0?Math.min(100,Math.abs(v)/max*100):0;
  return <div style={{marginBottom:10}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
      <span style={{fontSize:11,fontFamily:fonts.mono,color:col}}>{l}</span>
      <span style={{fontSize:12,fontFamily:fonts.mono,color:col,fontWeight:700}}>${Math.abs(v).toFixed(0)}</span>
    </div>
    <div style={{height:5,borderRadius:3,background:C.grid,overflow:"hidden"}}>
      <div style={{width:`${p}%`,height:"100%",background:col,borderRadius:3}}/>
    </div>
  </div>;
};

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function TradeVisualizer({ trade, context, allTrades, currentIndex, onNavigate, onClose }) {
  const [dark,     setDark]     = useState(true);
  const [activeTf, setActiveTf] = useState(() => context?.entry_tf||"M5");
  const [chartData,setChartData]= useState({});
  const [allSigs,  setAllSigs]  = useState({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [activeSigTfs, setActiveSigTfs] = useState(new Set()); // per-TF toggle
  const C = dark ? DARK : LIGHT;

  const tfs = useMemo(() => {
    const b=[context?.entry_tf,context?.context_tf,context?.regime_tf].filter(Boolean);
    if(!b.includes("D")) b.push("D");
    return b;
  }, [context?.entry_tf, context?.context_tf, context?.regime_tf]);

  useEffect(() => { if(context?.entry_tf) setActiveTf(context.entry_tf); }, [context?.entry_tf]);

  // ★ KEY FIX: stable string key derived from actual values (NOT context object reference)
  // This prevents re-fetching on every parent re-render caused by useApi polling
  const chartKey = useMemo(() => {
    if(!trade?.entry_time||!context?.instrument) return null;
    return `${context.instrument}|${context.entry_tf||"M5"}|${context.context_tf||"H1"}|${context.regime_tf||"H4"}|${trade.entry_time}|${trade.exit_time}`;
  }, [trade?.entry_time, trade?.exit_time, context?.instrument, context?.entry_tf, context?.context_tf, context?.regime_tf]);

  // Fetch chart data — only when chartKey changes (stable!)
  useEffect(() => {
    if(!chartKey||!context?.instrument) return;
    setLoading(true); setError(null); setChartData({});
    const base=context.apiBase||API_BASE||"";
    const p=new URLSearchParams({instrument:context.instrument,entry_tf:context.entry_tf||"M5",context_tf:context.context_tf||"H1",regime_tf:context.regime_tf||"H4",additional_tf:"D",entry_time:trade.entry_time,exit_time:trade.exit_time,bars_before:120,bars_after:80});
    fetch(`${base}/chart-data?${p}`)
      .then(r=>{if(!r.ok)throw new Error(`HTTP ${r.status}`);return r.json();})
      .then(d=>{setChartData(d);setLoading(false);})
      .catch(e=>{setError(e.message);setLoading(false);});
  }, [chartKey]); // ← ONLY chartKey, not context object

  // Fetch BoS/ChoCH for multiple TFs — also keyed to chartKey
  useEffect(() => {
    if(!chartKey||!context?.instrument) return;
    const base=context.apiBase||API_BASE||"";
    const sigTFs=[context.entry_tf||"M5", context.context_tf||"H1", "D"].filter((v,i,a)=>a.indexOf(v)===i);
    setAllSigs({});
    sigTFs.forEach(tf => {
      const p=new URLSearchParams({instrument:context.instrument,entry_tf:tf,strategy_file:context.strategy_file||"",entry_time:trade.entry_time,exit_time:trade.exit_time,bars_before:120,bars_after:80});
      fetch(`${base}/trade-signals?${p}`)
        .then(r=>r.ok?r.json():null)
        .then(d=>{ if(d?.signals?.length) setAllSigs(prev=>({...prev,[tf]:d.signals})); })
        .catch(()=>{});
    });
  }, [chartKey]); // ← stable

  useEffect(() => {
    const h=e=>{
      if(e.key==="Escape"){onClose?.();return;}
      if(e.key==="ArrowLeft"&&currentIndex>0) onNavigate?.(currentIndex-1);
      if(e.key==="ArrowRight"&&currentIndex<(allTrades?.length??1)-1) onNavigate?.(currentIndex+1);
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  }, [currentIndex,allTrades,onNavigate,onClose]);

  if(!trade) return null;
  // Auto-enable signal TFs when allSigs loads
  useEffect(() => {
    const tfsWithSigs = Object.keys(allSigs).filter(tf => allSigs[tf]?.length > 0);
    if (tfsWithSigs.length > 0) setActiveSigTfs(new Set(tfsWithSigs));
  }, [allSigs]);

  const isWin=(trade.pnl??0)>=0, total=allTrades?.length??1;
  const candles=chartData[activeTf]??[];
  const initialSl=(trade.initial_sl&&Math.abs(trade.initial_sl)>0.001)?trade.initial_sl:null;
  const mae=Math.abs(trade.mae??0), mfe=Math.abs(trade.mfe??0), pAbs=Math.abs(trade.pnl??0);
  const excMax=Math.max(mae,mfe,pAbs,0.01);
  // Merge signals from all active TFs
  const currentSigs = Object.entries(allSigs).flatMap(([tf, sigs]) => activeSigTfs.has(tf) ? sigs : []);
  const hasPrev=currentIndex>0, hasNext=currentIndex<total-1;

  const btn=(en)=>({width:30,height:30,borderRadius:6,border:`1px solid ${C.border}`,background:"transparent",color:en?C.textS:C.textF,cursor:en?"pointer":"default",fontSize:17,display:"flex",alignItems:"center",justifyContent:"center"});

  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.72)",backdropFilter:"blur(8px)"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose?.();}}>

      <div style={{width:"calc(100vw - 14px)",height:"calc(100vh - 14px)",maxWidth:1900,maxHeight:1080,display:"flex",flexDirection:"column",background:C.bg,borderRadius:12,overflow:"hidden",boxShadow:"0 50px 150px rgba(0,0,0,0.85),0 0 0 1px rgba(128,128,128,0.1)"}}>

        {/* HEADER */}
        <div style={{height:50,display:"flex",alignItems:"center",padding:"0 14px",gap:10,flexShrink:0,background:C.header,borderBottom:`1px solid ${C.border}`}}>
          <button onClick={()=>hasPrev&&onNavigate?.(currentIndex-1)} style={btn(hasPrev)}>‹</button>
          <span style={{fontSize:11,color:C.textF,fontFamily:fonts.mono,minWidth:52,textAlign:"center"}}>{currentIndex+1} / {total}</span>
          <button onClick={()=>hasNext&&onNavigate?.(currentIndex+1)} style={btn(hasNext)}>›</button>
          <div style={{width:1,height:24,background:C.border}}/>
          <span style={{padding:"2px 10px",borderRadius:4,fontSize:12,fontWeight:700,fontFamily:fonts.mono,background:trade.direction==="long"?"rgba(13,148,136,0.15)":"rgba(220,38,38,0.15)",color:trade.direction==="long"?C.bull:C.bear}}>
            {(trade.direction??"").toUpperCase()}
          </span>
          <span style={{fontSize:18,fontWeight:800,color:C.textS,fontFamily:fonts.mono,letterSpacing:"-0.5px"}}>{context?.instrument??""}</span>
          <span style={{fontSize:19,fontWeight:900,fontFamily:fonts.mono,letterSpacing:"-0.5px",color:isWin?C.bull:C.bear}}>
            {(trade.pnl??0)>=0?"+":""}${Math.abs(trade.pnl??0).toFixed(2)}
          </span>
          <span style={{fontSize:11,color:C.textF,fontFamily:fonts.mono,padding:"2px 8px",borderRadius:4,background:C.border}}>{trade.exit_reason??""}</span>
          <div style={{flex:1}}/>
          {[[C.entry,"Entry",false],[C.exit,"Exit",true],...(initialSl?[[C.sl,"SL",true]]:[]),[C.bos,"BoS",false],[C.choch,"ChoCH",false]].map(([col,lbl,dsh])=>(
            <div key={lbl} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:16,height:0,borderTop:`2px ${dsh?"dashed":"solid"} ${col}`,opacity:0.8}}/>
              <span style={{fontSize:10,color:C.textF,fontFamily:fonts.mono}}>{lbl}</span>
            </div>
          ))}
          <div style={{width:1,height:24,background:C.border}}/>
          {/* Per-TF Signal toggles */}
          {Object.entries(allSigs).filter(([,s])=>s.length>0).map(([tf,sigs])=>{
            const on=activeSigTfs.has(tf);
            return <button key={tf} onClick={()=>setActiveSigTfs(p=>{const s=new Set(p);s.has(tf)?s.delete(tf):s.add(tf);return s;})}
              style={{padding:"3px 9px",borderRadius:5,border:`1px solid ${on?C.choch:C.border}`,background:on?"rgba(206,147,216,0.12)":"transparent",color:on?C.choch:C.textF,cursor:"pointer",fontSize:10.5,fontWeight:700,fontFamily:fonts.mono,display:"flex",alignItems:"center",gap:4}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:on?C.choch:C.textF,flexShrink:0}}/>
              {tf==="D"?"Daily":tf} <span style={{opacity:0.6}}>({sigs.length})</span>
            </button>;
          })}
          <div style={{width:1,height:24,background:C.border}}/>
          {tfs.map(tf=>(
            <button key={tf} onClick={()=>setActiveTf(tf)} style={{padding:"5px 13px",borderRadius:5,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:fonts.mono,background:activeTf===tf?(dark?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.08)"):"transparent",color:activeTf===tf?C.textS:C.textF,transition:"all 0.12s"}}>
              {tf==="D"?"Daily":tf}
            </button>
          ))}
          <div style={{width:1,height:24,background:C.border}}/>
          <button onClick={()=>setDark(d=>!d)} style={{...btn(true),fontSize:14}}>{dark?"☀":"🌙"}</button>
          <button onClick={onClose} style={{...btn(true),fontSize:16}}>✕</button>
        </div>

        {/* BODY */}
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>
          {/* Sidebar */}
          <div style={{width:214,flexShrink:0,background:C.panel,borderRight:`1px solid ${C.border}`,padding:"20px 18px 16px",overflowY:"auto",color:C.textS}}>
            <SB t="Entry" C={C}>
              <SR l="Time"  v={fT(trade.entry_time)} C={C}/>
              <SR l="Price" v={Number(trade.entry_price??0).toLocaleString(undefined,{minimumFractionDigits:2})} c={C.entry} C={C}/>
              <SR l="Qty"   v={trade.qty??1} C={C}/>
            </SB>
            <SB t="Exit" C={C}>
              <SR l="Time"   v={fT(trade.exit_time)} C={C}/>
              <SR l="Price"  v={Number(trade.exit_price??0).toLocaleString(undefined,{minimumFractionDigits:2})} c={C.exit} C={C}/>
              <SR l="Reason" v={trade.exit_reason??'—'} C={C}/>
              <SR l="Dur"    v={fD(trade.entry_time,trade.exit_time)} C={C}/>
            </SB>
            <SB t="Risk / SL" C={C}>
              {initialSl
                ?<SR l="Initial SL" v={Number(initialSl).toLocaleString(undefined,{minimumFractionDigits:2})} c={C.sl} C={C}/>
                :<div style={{fontSize:11,color:C.textF,fontStyle:"italic",fontFamily:fonts.mono}}>No SL stored (old run)</div>
              }
            </SB>
            <SB t="Result" C={C}>
              <SR l="P&L"  v={`${isWin?"+":""}$${Math.abs(trade.pnl??0).toFixed(2)}`} c={isWin?C.bull:C.bear} C={C}/>
              <SR l="MAE"  v={`-$${mae.toFixed(2)}`} c={C.bear} C={C}/>
              <SR l="MFE"  v={`+$${mfe.toFixed(2)}`} c={C.bull} C={C}/>
              <SR l="Bars" v={trade.bars_in_trade||"—"} C={C}/>
              <SR l="ATR"  v={trade.atr_at_entry?Number(trade.atr_at_entry).toFixed(3):"—"} C={C}/>
            </SB>
            <div>
              <SL t="Excursion" C={C}/>
              <XBar l="MFE" v={mfe} max={excMax} col={C.bull} C={C}/>
              <XBar l="MAE" v={mae} max={excMax} col={C.bear} C={C}/>
              <XBar l="P&L" v={pAbs} max={excMax} col={isWin?C.bull:C.bear} C={C}/>
            </div>
          </div>

          {/* Chart */}
          <div style={{flex:1,overflow:"hidden",background:C.bg}}>
            {loading?(
              <div style={{height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14}}>
                <div style={{width:30,height:30,border:`2px solid ${C.border}`,borderTopColor:C.textF,borderRadius:"50%",animation:"tvSpin 0.75s linear infinite"}}/>
                <span style={{fontSize:13,color:C.textF,fontFamily:fonts.mono}}>Loading chart…</span>
              </div>
            ):error?(
              <div style={{height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
                <span style={{fontSize:22}}>⚠</span>
                <span style={{fontSize:14,color:C.bear,fontFamily:fonts.mono}}>Chart unavailable</span>
                <span style={{fontSize:11,color:C.textF,fontFamily:fonts.mono}}>{error}</span>
              </div>
            ):candles.length>0?(
              <CandleChart key={chartKey} candles={candles} entryTime={trade.entry_time} exitTime={trade.exit_time}
                entryPrice={trade.entry_price} exitPrice={trade.exit_price}
                initialSl={initialSl} direction={trade.direction} tf={activeTf}
                signals={currentSigs} C={C}/>
            ):(
              <div style={{height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:13,color:C.textF,fontFamily:fonts.mono}}>No data for {activeTf}</span>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div style={{height:28,display:"flex",alignItems:"center",padding:"0 14px",gap:18,flexShrink:0,background:C.header,borderTop:`1px solid ${C.border}`}}>
          {[["←→","navigate"],["Esc","close"],["Scroll","zoom"],["Drag","pan"]].map(([k,d])=>(
            <span key={k} style={{fontSize:10,color:C.textF,fontFamily:fonts.mono}}>
              <kbd style={{padding:"1px 5px",borderRadius:3,background:C.border,color:C.text}}>{k}</kbd> {d}
            </span>
          ))}
          <div style={{flex:1}}/>
          <span style={{fontSize:10,color:C.textF,fontFamily:fonts.mono}}>#{trade.trade_id??currentIndex+1}</span>
        </div>
      </div>
    </div>
  );
}
